/**
 * AbilitySpec — domain contract for champion ability data (AB-02 + LoL-shaped defaults).
 * Identity fields required; timing/geometry/DoT/CC optional via ABILITY_SPEC_DEFAULTS.
 */

import {
  ABILITY_AFFECTS,
  ABILITY_CC_TYPES,
  ABILITY_CD_START,
  ABILITY_COST_TYPES,
  ABILITY_DAMAGE_TYPES,
  ABILITY_SPEC_DEFAULTS,
  ABILITY_SPELL_EFFECTS
} from './abilitySpecDefaults.js';

export const TARGETING_TYPES = new Set([
  'self_buff',
  'targeted',
  'skillshot_line',
  'skillshot_cone',
  'aoe_self',
  'ground_aoe',
  'global_vision',
  'dash',
  'blink',
  'dual_blade'
]);

/** Player-facing families: from champion, ground on map, self, unit, global. */
export const TARGETING_FAMILY = Object.freeze({
  self_buff: 'self',
  aoe_self: 'self',
  targeted: 'unit',
  skillshot_line: 'from_caster',
  skillshot_cone: 'from_caster',
  dash: 'from_caster',
  blink: 'from_caster',
  dual_blade: 'from_caster',
  ground_aoe: 'ground',
  global_vision: 'global'
});

export function targetingFamily(targeting) {
  return TARGETING_FAMILY[targeting] || null;
}

const SLOTS = new Set(['Q', 'W', 'E', 'R']);

const REQUIRED = [
  'id',
  'champKey',
  'slot',
  'name',
  'targeting',
  'cooldown',
  'manaCost',
  'vfxId'
];

function assertRequired(raw) {
  for (const key of REQUIRED) {
    if (raw[key] === undefined || raw[key] === null) {
      throw new Error(`AbilitySpec missing required field: ${key}`);
    }
  }
  if (!TARGETING_TYPES.has(raw.targeting)) {
    throw new Error(`AbilitySpec unknown targeting: ${raw.targeting}`);
  }
  if (!SLOTS.has(raw.slot)) {
    throw new Error(`AbilitySpec invalid slot: ${raw.slot}`);
  }
}

function assertCc(cc) {
  if (cc === null || cc === undefined) return;
  if (typeof cc !== 'object' || !ABILITY_CC_TYPES.has(cc.type)) {
    throw new Error('AbilitySpec cc.type invalid');
  }
  if (!Number.isFinite(cc.duration) || cc.duration < 0) {
    throw new Error('AbilitySpec cc.duration must be >= 0');
  }
}

function assertDot(dot) {
  if (dot === null || dot === undefined) return;
  if (typeof dot !== 'object') {
    throw new Error('AbilitySpec dot must be object or null');
  }
  if (!Number.isFinite(dot.duration) || dot.duration < 0) {
    throw new Error('AbilitySpec dot.duration must be >= 0');
  }
  if (!Number.isFinite(dot.tickInterval) || dot.tickInterval <= 0) {
    throw new Error('AbilitySpec dot.tickInterval must be > 0');
  }
}

function assertEnums(merged) {
  if (!ABILITY_COST_TYPES.has(merged.costType)) {
    throw new Error(`AbilitySpec unknown costType: ${merged.costType}`);
  }
  if (!ABILITY_CD_START.has(merged.cdStart)) {
    throw new Error(`AbilitySpec unknown cdStart: ${merged.cdStart}`);
  }
  if (!ABILITY_AFFECTS.has(merged.affects)) {
    throw new Error(`AbilitySpec unknown affects: ${merged.affects}`);
  }
  if (merged.damageType !== null && merged.damageType !== undefined
    && !ABILITY_DAMAGE_TYPES.has(merged.damageType)) {
    throw new Error(`AbilitySpec unknown damageType: ${merged.damageType}`);
  }
  if (merged.spellEffects !== null && merged.spellEffects !== undefined
    && !ABILITY_SPELL_EFFECTS.has(merged.spellEffects)) {
    throw new Error(`AbilitySpec unknown spellEffects: ${merged.spellEffects}`);
  }
  assertCc(merged.cc);
  assertDot(merged.dot);
}

function freezeDeep(value) {
  if (value === null || value === undefined || typeof value !== 'object') return value;
  if (Object.isFrozen(value)) return value;
  for (const key of Object.keys(value)) {
    freezeDeep(value[key]);
  }
  return Object.freeze(value);
}

/**
 * @param {object} raw
 * @returns {Readonly<object>}
 */
export function createAbilitySpec(raw) {
  if (!raw || typeof raw !== 'object') {
    throw new Error('AbilitySpec requires an object');
  }
  assertRequired(raw);
  const merged = {
    ...ABILITY_SPEC_DEFAULTS,
    ...raw,
    cc: raw.cc !== undefined ? raw.cc : ABILITY_SPEC_DEFAULTS.cc,
    dot: raw.dot !== undefined ? raw.dot : ABILITY_SPEC_DEFAULTS.dot
  };
  assertEnums(merged);
  return freezeDeep(merged);
}

export function isValidAbilitySpec(raw) {
  try {
    createAbilitySpec(raw);
    return true;
  } catch {
    return false;
  }
}

export { ABILITY_SPEC_DEFAULTS } from './abilitySpecDefaults.js';
